/**
 * Business Continuity Plan Template Generator
 * 
 * This module generates the BCP content based on the user's form data.
 */

export const generateBCPDocument = (formData) => {
  let document = `# Business Continuity Plan\n\n`;
  
  // Company Information
  if (formData.companyInfo?.firmName) {
    document += `**Firm Name:** ${formData.companyInfo.firmName}\n\n`;
  }

  // Firm Policy
  document += `## Firm Policy\n\n`;
  if (formData.firmPolicy?.policyStatement) {
    document += `${formData.firmPolicy.policyStatement}\n\n`;
  } else {
    document += `Our BCP's primary objectives are to continue providing services to our customers, protect the health and safety of our employees, and fulfill our legal and regulatory obligations. In the event that we determine we are unable to continue our business we will assure customers prompt access to their funds and securities.\n\n`;
  }

  if (formData.firmPolicy?.planApprover) {
    document += `**Person responsible for approving the BCP:** ${formData.firmPolicy.planApprover}\n\n`;
  }

  if (formData.firmPolicy?.planLocation) {
    document += `**BCP Storage Location:** ${formData.firmPolicy.planLocation}\n\n`;
  }

  document += `### Significant Business Disruptions (SBDs)\n\n`;
  document += `An SBD may affect only our firm (e.g., a fire in our office building or cyber event) or may be widespread affecting several firms or the operation of the securities markets (e.g., a terrorist attack, a natural disaster or a pandemic). Our response will vary depending on the severity of the SBD, which may include greater reliance on other organizations and systems, especially on the capabilities of our clearing firm.\n\n`;

  // Emergency Contacts
  if (formData.emergencyContacts) {
    document += `## Emergency Contacts\n\n`;

    // Primary Emergency Contact
    if (formData.emergencyContacts.contact1) {
      document += `### Primary Emergency Contact\n\n`;
      const primary = formData.emergencyContacts.contact1;
      if (primary.name) document += `**Name:** ${primary.name}\n`;
      if (primary.title) document += `**Title:** ${primary.title}\n`;
      if (primary.mailing) document += `**Address:** ${primary.mailing}\n`;
      if (primary.email) document += `**Email:** ${primary.email}\n`;
      if (primary.phone) document += `**Phone:** ${primary.phone}\n`;
      if (primary.fax) document += `**Fax:** ${primary.fax}\n`;
      document += `\n`;
    }

    // Secondary Emergency Contact
    if (formData.emergencyContacts.contact2) {
      document += `### Secondary Emergency Contact\n\n`;
      const secondary = formData.emergencyContacts.contact2;
      if (secondary.name) document += `**Name:** ${secondary.name}\n`;
      if (secondary.title) document += `**Title:** ${secondary.title}\n`;
      if (secondary.mailing) document += `**Address:** ${secondary.mailing}\n`;
      if (secondary.email) document += `**Email:** ${secondary.email}\n`;
      if (secondary.phone) document += `**Phone:** ${secondary.phone}\n`;
      if (secondary.fax) document += `**Fax:** ${secondary.fax}\n`;
      document += `\n`;
    }

    // FINRA Contact System
    if (formData.emergencyContacts.fcsResponsible) {
      document += `### FINRA Contact System\n\n`;
      document += `**Person responsible for updating FINRA Contact System (FCS):** ${formData.emergencyContacts.fcsResponsible}\n\n`;
    }
  }

  // Business Description
  if (formData.businessDescription) {
  document += `## Business Description\n\n`;
  
    if (formData.businessDescription.businessTypes) {
      document += `### Types of Securities\n\n`;
      document += `Our firm deals with the following types of securities:\n`;
      
      // Map the values to their display labels
      const typeLabels = {
        equity: "Equity securities",
        fixed: "Fixed income securities",
        derivatives: "Derivatives",
        privatePlacements: "Private placements",
        other: "Other"
      };
      
      const types = formData.businessDescription.businessTypes;
      
      if (Array.isArray(types)) {
        types.forEach(type => {
          document += `- ${typeLabels[type] || type}\n`;
        });
      }
      document += `\n`;

      // If "other" is selected and there's additional info
      if (types.includes('other') && formData.businessDescription.otherBusinessTypes) {
        document += `Other types: ${formData.businessDescription.otherBusinessTypes}\n\n`;
      }
    }

    if (formData.businessDescription.customerTypes) {
      document += `### Customer Types\n\n`;
      const customerTypeLabels = {
        retail: "retail",
        institutional: "institutional",
        both: "both retail and institutional"
      };
      document += `We serve ${customerTypeLabels[formData.businessDescription.customerTypes] || formData.businessDescription.customerTypes} customers.\n\n`;
    }

    if (formData.businessDescription.clearingFirm) {
      document += `### Clearing Firm Information\n\n`;
      const cf = formData.businessDescription.clearingFirm;
      if (cf.name) document += `**Name:** ${cf.name}\n`;
      if (cf.address) document += `**Address:** ${cf.address}\n`;
      if (cf.phone) document += `**Phone:** ${cf.phone}\n`;
      if (cf.email) document += `**Email:** ${cf.email}\n`;
      if (cf.website) document += `**Website:** ${cf.website}\n`;
      
      // Primary Contact
      document += `\n**Primary Contact:**\n`;
      if (cf.primaryContact) document += `**Name:** ${cf.primaryContact}\n`;
      if (cf.primaryContactPhone) document += `**Phone:** ${cf.primaryContactPhone}\n`;
      if (cf.primaryContactEmail) document += `**Email:** ${cf.primaryContactEmail}\n`;
      
      // Alternate Contact
      document += `\n**Alternate Contact:**\n`;
      if (cf.alternateContact) document += `**Name:** ${cf.alternateContact}\n`;
      if (cf.alternateContactPhone) document += `**Phone:** ${cf.alternateContactPhone}\n`;
      if (cf.alternateContactEmail) document += `**Email:** ${cf.alternateContactEmail}\n`;
      
      document += `\n`;
    }
  }

  // Office Locations
  if (formData.officeLocations?.locations) {
    document += `## Office Locations\n\n`;
    formData.officeLocations.locations.forEach((location, index) => {
      document += `### Office ${index + 1}\n\n`;
      if (location.name) document += `**Name/Number:** ${location.name}\n`;
      if (location.address) document += `**Address:** ${location.address}\n`;
      if (location.phone) document += `**Phone:** ${location.phone}\n`;
      if (location.contact) document += `**Manager/Contact:** ${location.contact}\n`;
      if (location.contactEmail) document += `**Contact Email:** ${location.contactEmail}\n`;
      if (location.contactPhone) document += `**Contact Phone:** ${location.contactPhone}\n`;
      if (location.transportation) document += `**Uses Public Transportation:** ${location.transportation === 'yes' ? 'Yes' : 'No'}\n`;
      
      if (location.functions) {
        document += `\n**Functions:**\n`;
        const functionLabels = {
          orderTaking: "Order taking",
          orderEntry: "Order entry",
          execution: "Order execution",
          other: "Other"
        };
        
        if (Array.isArray(location.functions)) {
          location.functions.forEach(func => {
            document += `- ${functionLabels[func] || func}\n`;
          });
        }
        
        if (location.functions.includes('other') && location.otherFunctions) {
          document += `Other functions: ${location.otherFunctions}\n`;
        }
      }
      
      if (location.alternateLocation) {
        document += `\n**Alternate/Back-up Location:** ${location.alternateLocation}\n`;
      }
      
      document += `\n`;
    });
  }

  // Alternate Physical Locations
  if (formData.alternateLocations) {
    document += `## Alternate Physical Locations of Employees\n\n`;
    
    if (formData.alternateLocations.alternateLocationStrategy) {
      document += `### Strategy for Alternate Work Locations\n\n`;
      document += `${formData.alternateLocations.alternateLocationStrategy}\n\n`;
    }

    if (formData.alternateLocations.remoteWorkCybersecurity) {
      document += `### Cybersecurity Measures for Remote Work\n\n`;
      const measures = formData.alternateLocations.remoteWorkCybersecurity;
      const measureLabels = {
        vpn: "Ensuring VPNs and remote access systems are properly patched",
        entitlements: "Checking that system entitlements are current",
        mfa: "Multi-factor authentication for remote system access",
        education: "Regular cybersecurity education and reminders",
        other: "Other measures"
      };

      if (Array.isArray(measures)) {
        measures.forEach(measure => {
          document += `- ${measureLabels[measure] || measure}\n`;
        });
        document += `\n`;

        if (measures.includes('other') && formData.alternateLocations.otherCybersecurityMeasures) {
          document += `Other cybersecurity measures: ${formData.alternateLocations.otherCybersecurityMeasures}\n\n`;
        }
      }
    }
  }

  // Customers' Access to Funds and Securities
  if (formData.customerAccess) {
    document += `## Customers' Access to Funds and Securities\n\n`;

    if (formData.customerAccess.customerAccessMethod) {
      document += `### Customer Notification Methods\n\n`;
      document += `We will notify customers about accessing their funds and securities through the following methods:\n`;
      
      const methodLabels = {
        website: "Post on website",
        customerPortal: "Post on customer portal",
        email: "Send email to customers",
        phone: "Phone calls to customers",
        other: "Other method"
      };

      if (Array.isArray(formData.customerAccess.customerAccessMethod)) {
        formData.customerAccess.customerAccessMethod.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.customerAccess.customerAccessMethod.includes('other') && 
            formData.customerAccess.otherCustomerAccessMethod) {
          document += `Other notification method: ${formData.customerAccess.otherCustomerAccessMethod}\n\n`;
        }
      }
    }

    if (formData.customerAccess.clearingFirmCustomerContact) {
      document += `### Clearing Firm Contact Information\n\n`;
      document += `The following information will be provided to customers if they need to contact our clearing firm directly:\n\n`;
      document += `${formData.customerAccess.clearingFirmCustomerContact}\n\n`;
    }
  }

  // Data Back-Up and Recovery
  if (formData.dataBackup) {
    document += `## Data Back-Up and Recovery\n\n`;

    if (formData.dataBackup.primaryRecordsFormat) {
      document += `### Primary Records Format\n\n`;
      const formatLabels = {
        electronic: "Electronic form",
        paper: "Paper form",
        both: "Both electronic and paper form"
      };
      document += `Our primary/original books and records are maintained in ${formatLabels[formData.dataBackup.primaryRecordsFormat] || formData.dataBackup.primaryRecordsFormat}.\n\n`;
    }

    if (formData.dataBackup.primaryRecordsLocation) {
      document += `**Primary Records Location:** ${formData.dataBackup.primaryRecordsLocation}\n\n`;
    }

    if (formData.dataBackup.primaryRecordsResponsible) {
      document += `**Person Responsible for Primary Records:** ${formData.dataBackup.primaryRecordsResponsible}\n\n`;
    }

    if (formData.dataBackup.exclusiveDocuments) {
      document += `### Documents Not Transmitted to Clearing Firm\n\n`;
      document += `${formData.dataBackup.exclusiveDocuments}\n\n`;
    }

    if (formData.dataBackup.backupRecordsFormat) {
      document += `### Back-up Records Format\n\n`;
      const formatLabels = {
        electronic: "Electronic form",
        paper: "Paper form",
        both: "Both electronic and paper form"
      };
      document += `Our back-up records are maintained in ${formatLabels[formData.dataBackup.backupRecordsFormat] || formData.dataBackup.backupRecordsFormat}.\n\n`;
    }

    if (formData.dataBackup.backupRecordsLocation) {
      document += `**Back-up Records Location:** ${formData.dataBackup.backupRecordsLocation}\n\n`;
    }

    if (formData.dataBackup.backupRecordsResponsible) {
      document += `**Person Responsible for Back-up Records:** ${formData.dataBackup.backupRecordsResponsible}\n\n`;
    }

    // Paper backup details if applicable
    if (['paper', 'both'].includes(formData.dataBackup.backupRecordsFormat)) {
      if (formData.dataBackup.paperBackupFrequency) {
        document += `### Paper Records Backup\n\n`;
        document += `**Backup Frequency:** ${formData.dataBackup.paperBackupFrequency}\n\n`;
      }
      if (formData.dataBackup.paperBackupProcess) {
        document += `**Backup Process:** ${formData.dataBackup.paperBackupProcess}\n\n`;
      }
    }

    // Electronic backup details if applicable
    if (['electronic', 'both'].includes(formData.dataBackup.backupRecordsFormat)) {
      if (formData.dataBackup.electronicBackupFrequency) {
        document += `### Electronic Records Backup\n\n`;
        document += `**Backup Frequency:** ${formData.dataBackup.electronicBackupFrequency}\n\n`;
      }
      if (formData.dataBackup.electronicBackupProcess) {
        document += `**Backup Process:** ${formData.dataBackup.electronicBackupProcess}\n\n`;
      }
    }

    // Third-party recordkeeping service
    if (formData.dataBackup.usesRecordkeepingService === 'yes' && formData.dataBackup.recordkeepingService) {
      document += `### Third-Party Recordkeeping Service\n\n`;
      const service = formData.dataBackup.recordkeepingService;
      if (service.name) document += `**Service Name:** ${service.name}\n`;
      if (service.address) document += `**Address:** ${service.address}\n`;
      if (service.phone) document += `**Phone:** ${service.phone}\n`;
      document += `\n`;
    }
  }

  // Operational Risk
  if (formData.operationalRisk) {
    document += `## Operational Risk\n\n`;

    if (formData.operationalRisk.communicationMethods) {
      document += `### Alternative Communication Methods\n\n`;
      document += `During an SBD, we will use the following alternative methods of communication:\n`;
      
      const methodLabels = {
        website: "Website",
        phone: "Phone",
        email: "Email",
        other: "Other"
      };

      if (Array.isArray(formData.operationalRisk.communicationMethods)) {
        formData.operationalRisk.communicationMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.operationalRisk.communicationMethods.includes('other') && 
            formData.operationalRisk.otherCommunicationMethods) {
          document += `Other communication methods: ${formData.operationalRisk.otherCommunicationMethods}\n\n`;
        }
      }
    }

    if (formData.operationalRisk.customerNotification) {
      document += `### Customer Notification Methods\n\n`;
      document += `We will notify customers about accessing their funds and securities through:\n`;
      
      const notificationLabels = {
        website: "Post on website",
        portal: "Post on customer portal",
        email: "Send email to customers",
        other: "Other"
      };

      if (Array.isArray(formData.operationalRisk.customerNotification)) {
        formData.operationalRisk.customerNotification.forEach(method => {
          document += `- ${notificationLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.operationalRisk.customerNotification.includes('other') && 
            formData.operationalRisk.otherCustomerNotification) {
          document += `Other notification methods: ${formData.operationalRisk.otherCustomerNotification}\n\n`;
        }
      }
    }
  }

  // Financial and Credit Risk
  if (formData.financialCreditRisk) {
    document += `## Financial and Credit Risk\n\n`;

    if (formData.financialCreditRisk.financialProcedures) {
      document += `### Financial Procedures During SBD\n\n`;
      document += `${formData.financialCreditRisk.financialProcedures}\n\n`;
    }
  }

  // Mission Critical Systems
  if (formData.missionCriticalSystems) {
    document += `## Mission Critical Systems\n\n`;

    if (formData.missionCriticalSystems.criticalSystems) {
      document += `### Critical Systems Overview\n\n`;
      formData.missionCriticalSystems.criticalSystems.forEach((system, index) => {
        document += `#### System ${index + 1}\n`;
        if (system.name) document += `**Name:** ${system.name}\n`;
        if (system.function) document += `**Function:** ${system.function}\n`;
        if (system.provider) document += `**Provider:** ${system.provider}\n`;
        if (system.contactInfo) document += `**Contact Information:** ${system.contactInfo}\n\n`;
      });
    }

    if (formData.missionCriticalSystems.orderTakingMethods) {
      document += `### Order Taking Methods\n\n`;
      document += `Current methods for receiving customer orders:\n`;
      
      const methodLabels = {
        phone: "Phone",
        fax: "Fax",
        email: "Email",
        website: "Website",
        inperson: "In-person visits",
        other: "Other"
      };

      if (Array.isArray(formData.missionCriticalSystems.orderTakingMethods)) {
        formData.missionCriticalSystems.orderTakingMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.missionCriticalSystems.orderTakingMethods.includes('other') && 
            formData.missionCriticalSystems.otherOrderTakingMethods) {
          document += `Other methods: ${formData.missionCriticalSystems.otherOrderTakingMethods}\n\n`;
        }
      }
    }

    if (formData.missionCriticalSystems.orderEntrySystem) {
      document += `### Order Entry System\n\n`;
      document += `**Current System:** ${formData.missionCriticalSystems.orderEntrySystem}\n`;
      if (formData.missionCriticalSystems.orderEntryBackup) {
        document += `**Expected Recovery Time:** ${formData.missionCriticalSystems.orderEntryBackup}\n`;
      }
      if (formData.missionCriticalSystems.alternateOrderEntryMethods) {
        document += `**Alternative Methods:** ${formData.missionCriticalSystems.alternateOrderEntryMethods}\n`;
      }
      document += `\n`;
    }

    if (formData.missionCriticalSystems.orderExecutionMethods) {
      document += `### Order Execution Methods\n\n`;
      document += `${formData.missionCriticalSystems.orderExecutionMethods}\n\n`;
    }

    if (formData.missionCriticalSystems.otherServices) {
      document += `### Other Services\n\n`;
      document += `${formData.missionCriticalSystems.otherServices}\n\n`;
    }

    if (formData.missionCriticalSystems.clearingFirmServices) {
      document += `### Clearing Firm Services\n\n`;
      document += `Our clearing firm provides the following mission critical systems:\n`;
      
      const serviceLabels = {
        execution: "Order execution",
        comparison: "Order comparison",
        allocation: "Order allocation",
        accountMaintenance: "Customer account maintenance",
        accountAccess: "Customer account access",
        fundDelivery: "Delivery of funds and securities"
      };

      if (Array.isArray(formData.missionCriticalSystems.clearingFirmServices)) {
        formData.missionCriticalSystems.clearingFirmServices.forEach(service => {
          document += `- ${serviceLabels[service] || service}\n`;
        });
        document += `\n`;
      }
    }
  }

  // Alternate Communications
  if (formData.alternateCustomerCommunication) {
    document += `## Alternate Communications with Customers\n\n`;

    if (formData.alternateCustomerCommunication.customerCommMethods) {
      const methodLabels = {
        website: "Website",
        phone: "Phone",
        email: "Email",
        other: "Other"
      };

      document += `We will communicate with customers using:\n`;
      if (Array.isArray(formData.alternateCustomerCommunication.customerCommMethods)) {
        formData.alternateCustomerCommunication.customerCommMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.alternateCustomerCommunication.customerCommMethods.includes('other') && 
            formData.alternateCustomerCommunication.otherCustomerCommMethods) {
          document += `Other methods: ${formData.alternateCustomerCommunication.otherCustomerCommMethods}\n\n`;
        }
      }
    }
  }

  // Alternate Employee Communications
  if (formData.alternateEmployeeCommunication) {
    document += `## Alternate Communications with Employees\n\n`;

    if (formData.alternateEmployeeCommunication.employeeCommMethods) {
      const methodLabels = {
        website: "Website",
        phone: "Phone",
        email: "Email",
        text: "Text messages",
        other: "Other"
      };

      document += `We will communicate with employees using:\n`;
      if (Array.isArray(formData.alternateEmployeeCommunication.employeeCommMethods)) {
        formData.alternateEmployeeCommunication.employeeCommMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.alternateEmployeeCommunication.employeeCommMethods.includes('other') && 
            formData.alternateEmployeeCommunication.otherEmployeeCommMethods) {
          document += `Other methods: ${formData.alternateEmployeeCommunication.otherEmployeeCommMethods}\n\n`;
        }
      }
    }

    if (formData.alternateEmployeeCommunication.callTreeResponsible) {
      document += `### Call Tree\n\n`;
      document += `**Person responsible for initiating the call tree:** ${formData.alternateEmployeeCommunication.callTreeResponsible}\n\n`;
    }

    if (formData.alternateEmployeeCommunication.callTree) {
      document += `#### Call Tree Structure\n\n`;
      formData.alternateEmployeeCommunication.callTree.forEach((entry, index) => {
        document += `**Group ${index + 1}:**\n`;
        if (entry.caller) document += `- Caller: ${entry.caller}\n`;
        if (entry.recipients) document += `- Recipients: ${entry.recipients}\n\n`;
      });
    }
  }

  // Regulator Communications
  if (formData.regulatorCommunication) {
    document += `## Communications with Regulators\n\n`;

    if (formData.regulatorCommunication.regulators) {
      document += `### Regulators\n\n`;
      formData.regulatorCommunication.regulators.forEach((regulator, index) => {
        if (regulator.name) document += `${index + 1}. ${regulator.name}\n`;
      });
      document += `\n`;
    }

    if (formData.regulatorCommunication.regulatorCommMethods) {
      const methodLabels = {
        website: "Website",
        phone: "Phone",
        email: "Email",
        other: "Other"
      };

      document += `### Communication Methods\n\n`;
      document += `We will communicate with regulators using:\n`;
      if (Array.isArray(formData.regulatorCommunication.regulatorCommMethods)) {
        formData.regulatorCommunication.regulatorCommMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.regulatorCommunication.regulatorCommMethods.includes('other') && 
            formData.regulatorCommunication.otherRegulatorCommMethods) {
          document += `Other methods: ${formData.regulatorCommunication.otherRegulatorCommMethods}\n\n`;
        }
      }
    }
  }

  // Business Constituents
  if (formData.businessConstituents) {
    document += `## Business Constituents\n\n`;

    if (formData.businessConstituents.criticalBusinessConstituents) {
      document += `### Critical Business Constituents\n\n`;
      formData.businessConstituents.criticalBusinessConstituents.forEach((constituent, index) => {
        document += `#### Constituent ${index + 1}\n`;
        if (constituent.service) document += `**Service/Product:** ${constituent.service}\n`;
        if (constituent.supplier) document += `**Supplier:** ${constituent.supplier}\n`;
        if (constituent.alternate) document += `**Alternate Supplier:** ${constituent.alternate}\n\n`;
      });
    }
  }

  // Banks
  if (formData.banks) {
    document += `## Banks\n\n`;

    if (formData.banks.operatingBank) {
      document += `### Operating Account Bank\n\n`;
      const bank = formData.banks.operatingBank;
      if (bank.name) document += `**Name:** ${bank.name}\n`;
      if (bank.address) document += `**Address:** ${bank.address}\n`;
      if (bank.phone) document += `**Phone:** ${bank.phone}\n`;
      if (bank.contact) document += `**Contact Person:** ${bank.contact}\n\n`;
    }

    if (formData.banks.paibBank) {
      document += `### PAIB Account Bank\n\n`;
      const bank = formData.banks.paibBank;
      if (bank.name) document += `**Name:** ${bank.name}\n`;
      if (bank.address) document += `**Address:** ${bank.address}\n`;
      if (bank.phone) document += `**Phone:** ${bank.phone}\n`;
      if (bank.contact) document += `**Contact Person:** ${bank.contact}\n\n`;
    }

    if (formData.banks.alternateFinancing) {
      document += `### Alternative Financing Source\n\n`;
      const source = formData.banks.alternateFinancing;
      if (source.name) document += `**Name:** ${source.name}\n`;
      if (source.address) document += `**Address:** ${source.address}\n`;
      if (source.phone) document += `**Phone:** ${source.phone}\n\n`;
    }
  }

  // Regulatory Reporting
  if (formData.regulatoryReporting) {
    document += `## Regulatory Reporting\n\n`;

    if (formData.regulatoryReporting.regulatoryFilingMethods) {
      document += `### Filing Methods\n\n`;
      const methodLabels = {
        paper: "Paper copies in U.S. mail",
        electronic: "Electronic means",
        finraGateway: "FINRA Gateway",
        other: "Other"
      };

      document += `We fulfill regulatory reporting obligations through:\n`;
      if (Array.isArray(formData.regulatoryReporting.regulatoryFilingMethods)) {
        formData.regulatoryReporting.regulatoryFilingMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.regulatoryReporting.regulatoryFilingMethods.includes('other') && 
            formData.regulatoryReporting.otherRegulatoryFilingMethods) {
          document += `Other methods: ${formData.regulatoryReporting.otherRegulatoryFilingMethods}\n\n`;
        }
      }
    }

    if (formData.regulatoryReporting.regulatoryContacts) {
      document += `### Regulatory Contacts\n\n`;
      formData.regulatoryReporting.regulatoryContacts.forEach((contact, index) => {
        document += `#### Contact ${index + 1}\n`;
        if (contact.regulator) document += `**Regulator:** ${contact.regulator}\n`;
        if (contact.contactPerson) document += `**Contact Person:** ${contact.contactPerson}\n`;
        if (contact.contactInfo) document += `**Contact Information:** ${contact.contactInfo}\n\n`;
      });
    }
  }

  // Customer Disclosure
  if (formData.customerDisclosure) {
    document += `## Disclosure of Business Continuity Plan to Customers\n\n`;

    if (formData.customerDisclosure.disclosureMethods) {
      const methodLabels = {
        accountOpening: "At account opening",
        website: "Posted on website",
        mailedOnRequest: "Mailed upon request",
        other: "Other"
      };

      document += `We disclose our BCP to customers through:\n`;
      if (Array.isArray(formData.customerDisclosure.disclosureMethods)) {
        formData.customerDisclosure.disclosureMethods.forEach(method => {
          document += `- ${methodLabels[method] || method}\n`;
        });
        document += `\n`;

        if (formData.customerDisclosure.disclosureMethods.includes('other') && 
            formData.customerDisclosure.otherDisclosureMethods) {
          document += `Other methods: ${formData.customerDisclosure.otherDisclosureMethods}\n\n`;
        }
      }
    }
  }

  // Updates and Annual Review
  if (formData.updating) {
    document += `## Updates and Annual Review\n\n`;

    if (formData.updating.annualReviewDate) {
      const reviewDate = new Date(formData.updating.annualReviewDate);
      const formattedDate = reviewDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      document += `**Annual BCP Review Date:** ${formattedDate}\n`;
    }

    if (formData.updating.conductsTest) {
      document += `**Conducts Firm-wide BCP Test:** ${formData.updating.conductsTest === 'yes' ? 'Yes' : 'No'}\n\n`;
    }
  }

  // Senior Manager Approval
  if (formData.seniorManagerApproval) {
    document += `## Senior Manager Approval\n\n`;

    if (formData.seniorManagerApproval.approverName) {
      document += `**Name of Approver:** ${formData.seniorManagerApproval.approverName}\n`;
    }
    if (formData.seniorManagerApproval.approverTitle) {
      document += `**Title of Approver:** ${formData.seniorManagerApproval.approverTitle}\n`;
    }
    if (formData.seniorManagerApproval.approvalDate) {
      document += `**Date of Approval:** ${formData.seniorManagerApproval.approvalDate}\n\n`;
    }
  }

  // BCP Disclosure Statement
  if (formData.disclosureStatement) {
    document += `## BCP Disclosure Statement\n\n`;

    if (formData.disclosureStatement.firmEmergencyNumber) {
      document += `**Emergency Phone Number:** ${formData.disclosureStatement.firmEmergencyNumber}\n`;
    }
    if (formData.disclosureStatement.websiteURL) {
      document += `**Website:** ${formData.disclosureStatement.websiteURL}\n`;
    }
    if (formData.disclosureStatement.customerContactInfo) {
      document += `**Customer Contact Information:** ${formData.disclosureStatement.customerContactInfo}\n\n`;
    }

    if (formData.disclosureStatement.clearingFirmServices) {
      document += `### Clearing Firm Services During SBD\n\n`;
      const serviceLabels = {
        fundAccess: "Prompt access to funds and securities",
        orderEntry: "Enter orders",
        tradeProcessing: "Process trade-related transactions",
        cashTransfer: "Process cash transfers",
        securityTransfer: "Process security transfers",
        other: "Other"
      };

      document += `Our clearing firm can provide the following services during an SBD:\n`;
      if (Array.isArray(formData.disclosureStatement.clearingFirmServices)) {
        formData.disclosureStatement.clearingFirmServices.forEach(service => {
          document += `- ${serviceLabels[service] || service}\n`;
        });
        document += `\n`;

        if (formData.disclosureStatement.clearingFirmServices.includes('other') && 
            formData.disclosureStatement.otherClearingFirmServices) {
          document += `Other services: ${formData.disclosureStatement.otherClearingFirmServices}\n\n`;
        }
      }
    }

    if (formData.disclosureStatement.resumptionTimePeriod) {
      document += `**Expected Service Resumption Time:** ${formData.disclosureStatement.resumptionTimePeriod}\n\n`;
    }

    if (formData.disclosureStatement.additionalDisclosureInfo) {
      document += `### Additional Information\n\n`;
      document += `${formData.disclosureStatement.additionalDisclosureInfo}\n\n`;
    }
  }

  return document;
};